import { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

interface PendingPayment {
  id: string;
  type: 'subscription' | 'course' | 'resource' | 'connect';
  amount: number;
  description: string;
  createdAt: string;
  expiresAt?: string;
  metadata?: Record<string, any>;
  course?: any;
  resource?: any;
}

interface UsePendingPaymentReturn {
  pendingPayments: PendingPayment[];
  isLoading: boolean;
  error: string | null;
  checkPendingPayments: () => Promise<void>;
  clearPendingPayment: (paymentId: string) => Promise<void>;
  hasPendingPayments: boolean;
}

export function usePendingPayment(): UsePendingPaymentReturn {
  const [pendingPayments, setPendingPayments] = useState<PendingPayment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user] = useAuthState(auth);

  // Cloud Functions
  const getPendingPayments = httpsCallable(functions, 'getPendingPayments');
  const clearPendingPayment = httpsCallable(functions, 'clearPendingPayment');

  const checkPendingPayments = async () => {
    if (!user) return;

    // TODO: Implement getPendingPayments function in Firebase Functions
    // For now, return empty array to prevent CORS errors. Keep the same array
    // when it is already empty: a new [] every 30 s re-rendered the whole app.
    setPendingPayments((prev) => (prev.length ? [] : prev));
    setIsLoading(false);
    return;

    /* Commented out until function is deployed
    setIsLoading(true);
    setError(null);

    try {
      const result = await getPendingPayments({ limit: 10 });
      const data = result.data as Record<string, unknown>;

      if (data.success) {
        setPendingPayments(data.payments || []);
      } else {
        throw new Error('Failed to fetch pending payments');
      }
    } catch (err) {
      // Only log error in development, don't spam console in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching pending payments:', err);
      }
      setError(null); // Don't show error to user for pending payments
      setPendingPayments([]); // Clear any existing pending payments
    } finally {
      setIsLoading(false);
    }
    */
  };

  const clearPendingPaymentHandler = async (paymentId: string) => {
    // TODO: Implement clearPendingPayment function in Firebase Functions
    // For now, just remove from local state
    setPendingPayments(prev => prev.filter(p => p.id !== paymentId));
    return;

    /* Commented out until function is deployed
    setIsLoading(true);
    setError(null);

    try {
      const result = await clearPendingPayment({ paymentId });
      const data = result.data as Record<string, unknown>;

      if (data.success) {
        // Remove from local state
        setPendingPayments(prev => prev.filter(p => p.id !== paymentId));
      } else {
        throw new Error('Failed to clear pending payment');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error clearing pending payment:', err);
    } finally {
      setIsLoading(false);
    }
    */
  };

  // Auto-check for pending payments when user changes
  useEffect(() => {
    if (user) {
      checkPendingPayments();
    } else {
      setPendingPayments((prev) => (prev.length ? [] : prev));
    }
  }, [user]);

  // Set up periodic checking for pending payments
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      checkPendingPayments();
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [user]);

  return {
    pendingPayments,
    isLoading,
    error,
    checkPendingPayments,
    clearPendingPayment: clearPendingPaymentHandler,
    hasPendingPayments: pendingPayments.length > 0,
  };
}
